import { HatElement } from '@/components/hat-element.js';
// @ts-expect-error — no types for heic-convert/browser
import heicConvert from 'heic-convert/browser';

function isHeic(file: File): boolean {
    return (
        file.type === 'image/heic' ||
        file.type === 'image/heif' ||
        /\.heic$/i.test(file.name) ||
        /\.heif$/i.test(file.name)
    );
}

function canvasToJpeg(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d')!.drawImage(img, 0, 0);
            canvas.toBlob(blob => {
                if (!blob) { reject(new Error('canvas conversion failed')); return; }
                const name = file.name.replace(/\.[^.]+$/, '.jpg');
                resolve(new File([blob], name, { type: 'image/jpeg' }));
            }, 'image/jpeg', 0.85);
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
        img.src = url;
    });
}

class HatFileUpload extends HatElement {

    connectedCallback() {
    super.connectedCallback();
    this.render();
        this.bindEvents();
    }

    bindEvents() {
        const input = this.root.querySelector<HTMLInputElement>('#fileInput')!;

        input.addEventListener('change', async () => {
            const files = [...(input.files ?? [])];
            for (const file of files) {
                let outputFile: File;
                if (isHeic(file)) {
                    if (file.type === 'image/jpeg') {
                        // iOS already converted the HEIC to JPEG before handing it to the browser
                        outputFile = file;
                    } else {
                        try {
                            const arrayBuffer = await file.arrayBuffer();
                            const jpegBuffer = await heicConvert({
                                buffer: new Uint8Array(arrayBuffer),
                                format: 'JPEG',
                                quality: 0.85,
                            });
                            const name = file.name.replace(/\.(heic|heif)$/i, '.jpg');
                            outputFile = new File([jpegBuffer], name, { type: 'image/jpeg' });
                        } catch (err) {
                            console.error('HEIC conversion failed', file.name, err);
                            continue;
                        }
                    }
                } else if (file.type === 'image/png' || file.type === 'image/webp') {
                    try {
                        outputFile = await canvasToJpeg(file);
                    } catch (err) {
                        console.error('Image conversion failed', file.name, err);
                        continue;
                    }
                } else if (file.type === 'image/jpeg') {
                    outputFile = file;
                } else {
                    continue;
                }

                this.dispatchEvent(
                    new CustomEvent(
                        'photo-captured',
                        {
                            detail: outputFile,
                            bubbles: true,
                            composed: true
                        }
                    )
                );
            }

            input.value = '';
        });
    }

    render() {
        this.root.innerHTML = `
            <style>
                :host{
                    display:block;
                }

                .box{
                    border:2px dashed #ccc;
                    border-radius:12px;
                    padding:28px;
                    text-align:center;
                    background:#fafafa;
                }

                label{
                    display:inline-block;
                    background:#111;
                    color:white;
                    padding:10px 16px;
                    border-radius:10px;
                    cursor:pointer;
                    font-size:14px;
                }

                input{
                    display:none;
                }

                .hint{
                    margin-top:12px;
                    color:#666;
                    font-size:13px;
                }

            </style>

            <div class="box">
                <label for="fileInput">
                    Choose Photos
                </label>
                <input
                    id="fileInput"
                    type="file"
                    accept=".jpg,.jpeg,image/jpeg,.heic,.heif,image/heic,image/heif,.png,image/png,.webp,image/webp"
                    multiple
                />
                <div class="hint">
                    Supports JPG, PNG, WebP, and HEIC
                </div>
            </div>
        `;
    }
}

customElements.define('hat-file-upload', HatFileUpload);